/**
* Locale copy for the /{locale}/store pillar. Six locales — hand-written
* so each mirror ships unique text (not machine translation) and is safe
* for Google's duplicate-content de-dup across the hreflang cluster.
*/
export type StoreLocale = "en" | "zh" | "ja" | "ko" | "vi" | "id";

export interface StorePillarCopy {
  lang: string; // <html lang>
  hreflang: string; // hreflang value
  eyebrow: string;
  h1: string;
  lead: string;
  bundlesH2: string;
  categoriesH2: string;
  allH2: string; // includes {n}
  perBundle: string; // "{n} items · {d} days"
  metaTitle: string;
  metaDesc: string;
}

export const STORE_PILLAR_COPY: Record<StoreLocale, StorePillarCopy> = {
  en: {
    lang: "en", hreflang: "en",
    eyebrow: "NAATI Certified Translation Store",
    h1: "NAATI Certified Translation Store · Request a Quote",
    lead: "Certified translations of 32 Thai document types plus 6 workflow bundles for Skilled Migration, Student, Partner, Citizenship, Driver-Licence and Investor pathways. Each job is completed by a NAATI-credentialled translator and delivered with the translator's certification statement. Requirements differ between receiving authorities, so send us the destination agency and its current checklist and we will confirm the format, copies and any extra legalisation step before work begins.",
    bundlesH2: "Popular document bundles",
    categoriesH2: "Browse by category",
    allH2: "All products ({n})",
    perBundle: "{n} items · {d} days",
    metaTitle: "NAATI Certified Translation Store — Request a Quote",
    metaDesc: "Request a quote for NAATI Certified translations of Thai documents: 32 document types, six application bundles, turnaround and format confirmed in writing before work starts.",
  },
  zh: {
    lang: "zh-CN", hreflang: "zh-CN",
    eyebrow: "NAATI 认证翻译商店",
    h1: "NAATI 认证翻译商店 · 咨询报价",
    lead: "32 类泰国文件 NAATI 认证翻译，另有 6 个专为技术移民、学生、伴侣、入籍、驾照与投资签证准备的组合套餐。每份译件由持有 NAATI 资质的译员完成，并附译员认证声明。各受理机构的要求不同，请提供目标机构名称与最新要求清单，我们会在开始前以书面确认格式、份数与是否需要额外认证程序。",
    bundlesH2: "热门文件套餐",
    categoriesH2: "按类别浏览",
    allH2: "全部商品（{n}）",
    perBundle: "{n} 件 · {d} 天",
    metaTitle: "NAATI 认证翻译商店 — 咨询报价",
    metaDesc: "泰国文件 NAATI 认证翻译报价服务，涵盖 32 种文件类型与六类申请套餐；由持 NAATI 资质译员完成，开工前书面确认格式、份数与交付时间。",
  },
  ja: {
    lang: "ja-JP", hreflang: "ja-JP",
    eyebrow: "NAATI 認証翻訳ストア",
    h1: "NAATI 認証翻訳ストア · お見積り",
    lead: "32 種類のタイ書類の NAATI 認証翻訳と、Skilled Migration・Student・Partner・Citizenship・運転免許・投資家ビザ向けの 6 種類のバンドルを提供します。翻訳は NAATI 資格保有者が担当し、翻訳者の認証文を添えて納品します。受理機関ごとに要件が異なるため、提出先の名称と最新の要件一覧をお送りいただければ、着手前に書式・部数・追加の公証手続きの有無を書面でご確認します。",
    bundlesH2: "人気の書類バンドル",
    categoriesH2: "カテゴリから探す",
    allH2: "商品一覧（{n}）",
    perBundle: "{n} 点 · {d} 日",
    metaTitle: "NAATI 認証翻訳ストア — お見積り",
    metaDesc: "タイ書類の NAATI 認証翻訳をお見積り。32 種類の書類と六つの申請用バンドルに対応し、着手前に書式・部数・納期を書面でご確認します。",
  },
  ko: {
    lang: "ko-KR", hreflang: "ko-KR",
    eyebrow: "NAATI 공인 번역 스토어",
    h1: "NAATI 공인 번역 스토어 · 견적 문의",
    lead: "32종 태국 서류의 NAATI 공인 번역과 Skilled Migration·학생·배우자·시민권·운전면허·투자자 비자용 6종 번들을 제공합니다. 번역은 NAATI 자격 보유 번역사가 담당하며 번역사 인증문과 함께 전달됩니다. 접수 기관마다 요건이 다르므로 제출 기관명과 최신 요건 목록을 보내주시면 착수 전에 서식·부수·추가 인증 절차 여부를 서면으로 확인해 드립니다.",
    bundlesH2: "인기 서류 번들",
    categoriesH2: "카테고리별 보기",
    allH2: "전체 상품 ({n})",
    perBundle: "{n} 건 · {d} 일",
    metaTitle: "NAATI 공인 번역 스토어 — 견적 문의",
    metaDesc: "태국 서류 NAATI 공인 번역 견적 문의. 32종 서류와 여섯 가지 신청용 번들을 지원하며 착수 전 서식·부수·납기를 서면으로 확인합니다.",
  },
  vi: {
    lang: "vi-VN", hreflang: "vi-VN",
    eyebrow: "Cửa hàng dịch thuật NAATI",
    h1: "Cửa hàng dịch thuật NAATI · Nhận báo giá",
    lead: "Dịch công chứng NAATI cho 32 loại giấy tờ Thái Lan cùng 6 gói dành cho Skilled Migration, Student, Partner, Citizenship, đổi bằng lái và visa Đầu tư. Mỗi bản dịch do biên dịch viên có chứng nhận NAATI thực hiện và kèm tuyên bố xác nhận của biên dịch viên. Yêu cầu của từng cơ quan tiếp nhận khác nhau, vì vậy hãy gửi tên cơ quan và danh mục yêu cầu mới nhất để chúng tôi xác nhận bằng văn bản về định dạng, số bản và bước hợp pháp hóa bổ sung trước khi bắt đầu.",
    bundlesH2: "Gói hồ sơ phổ biến",
    categoriesH2: "Duyệt theo danh mục",
    allH2: "Tất cả sản phẩm ({n})",
    perBundle: "{n} mục · {d} ngày",
    metaTitle: "Cửa hàng dịch thuật NAATI — Nhận báo giá",
    metaDesc: "Nhận báo giá dịch thuật NAATI cho 32 loại giấy tờ Thái Lan và sáu gói hồ sơ; định dạng, số bản và thời gian giao được xác nhận bằng văn bản trước khi làm.",
  },
  id: {
    lang: "id-ID", hreflang: "id-ID",
    eyebrow: "Toko Terjemahan NAATI",
    h1: "Toko Terjemahan NAATI · Minta penawaran",
    lead: "Terjemahan NAATI Certified untuk 32 jenis dokumen Thailand plus 6 paket alur kerja untuk Skilled Migration, Pelajar, Pasangan, Kewarganegaraan, SIM dan Visa Investor. Setiap dokumen dikerjakan penerjemah bersertifikat NAATI dan dilengkapi pernyataan sertifikasi penerjemah. Persyaratan tiap instansi penerima berbeda, jadi kirimkan nama instansi tujuan beserta daftar persyaratan terbarunya agar kami mengonfirmasi format, jumlah salinan, dan langkah legalisasi tambahan secara tertulis sebelum pengerjaan.",
    bundlesH2: "Paket dokumen populer",
    categoriesH2: "Telusuri per kategori",
    allH2: "Semua produk ({n})",
    perBundle: "{n} item · {d} hari",
    metaTitle: "Toko Terjemahan NAATI — Minta penawaran",
    metaDesc: "Minta penawaran terjemahan NAATI untuk 32 jenis dokumen Thailand dan enam paket pengajuan; format, jumlah salinan, dan waktu kerja dikonfirmasi tertulis lebih dulu.",
  },
};
